import { useParams } from '@reach/router';
import { graphql } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC } from 'react';
import { useQuery } from 'urql';

import { limitPeriodDetailList_periodLimit } from '@/bits/limit-period-detail-list/component';
import { Card, CardBody, CardOptions, CardOptionsButton } from '@/components';
import { RefreshIcon } from '@/components/icons';
import { useTranslate } from '@/contexts';
import { limitFragment } from '@/fragments/Limits';
import { LimitTypeEnum } from '@/globalTypes';
import {
  PlayerGlobalLossLimitsBoxQuery,
  PlayerGlobalLossLimitsBoxQueryVariables,
} from './__generated__/component';
import { GlobalLossLimitBar } from './GlobalLossLimitBar';

export const Fragment = graphql`
  fragment SanityPlayerGlobalLossLimitsBlock on SanityPlayerGlobalLossLimitsBlock {
    title {
      ...SanityLocaleString
    }
    lossLimitsLabel {
      ...SanityLocaleString
    }
    weeklyLabel {
      ...SanityLocaleString
    }
    monthlyLabel {
      ...SanityLocaleString
    }
  }
`;

const QUERY = gql`
  query PlayerGlobalLossLimitsBox($playerId: ID!) {
    player(playerId: $playerId) {
      id
      walletWithGlobalLimits {
        id
        currency
        lossLimit {
          weekLimit {
            ...PeriodLimit
            ...LimitPeriodDetailList_periodLimit
          }
          monthLimit {
            ...PeriodLimit
            ...LimitPeriodDetailList_periodLimit
          }
        }
      }
    }
  }
  ${limitFragment}
  ${limitPeriodDetailList_periodLimit}
`;

const PlayerGlobalLossLimitsBlock: FC<{
  block: Queries.SanityPlayerLossLimitsBlockFragment;
}> = ({ block }) => {
  const { t } = useTranslate();
  const params = useParams();

  const [{ data, fetching }, refresh] = useQuery<
    PlayerGlobalLossLimitsBoxQuery,
    PlayerGlobalLossLimitsBoxQueryVariables
  >({
    query: QUERY,
    variables: {
      playerId: params.playerId,
    },
  });

  const lossLimit = data?.player.walletWithGlobalLimits?.lossLimit;

  return (
    <Card
      size="lg"
      title={t(block.title)}
      options={
        <CardOptions>
          <CardOptionsButton
            className="flex"
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          <div className="col-span-full">
            <label className="text-sm text-gray-500 font-semibold">
              {t(block.lossLimitsLabel)}
            </label>
            <div className="grid gap-2 pt-1">
              <GlobalLossLimitBar
                currency={data?.player.walletWithGlobalLimits?.currency}
                fetching={fetching}
                limit={lossLimit?.weekLimit}
                label={block.weeklyLabel}
                playerId={data?.player.id}
                limitPeriodType={LimitTypeEnum.Week}
              />
              <GlobalLossLimitBar
                currency={data?.player.walletWithGlobalLimits?.currency}
                fetching={fetching}
                limit={lossLimit?.monthLimit}
                label={block.monthlyLabel}
                playerId={data?.player.id}
                limitPeriodType={LimitTypeEnum.Month}
              />
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerGlobalLossLimitsBlock;
